import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, TriangleAlert, X } from "lucide-react";
import axios from "axios";
import BookingPopup from "./BookLesson";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

type Subject = {
  id: number;
  name: string;
  price_in_cents?: number;
};

type Student = {
  id: number;
  customer: {
    id: number;
    name: string;
    first_name: string;
  };
};

const BookLessonModal = ({
  isOpen,
  onClose,
  setUpdateUi,
  updateUi,
  timezone,
}: {
  isOpen: boolean;
  onClose: () => void;
  setUpdateUi: any;
  updateUi: any;
  timezone: string;
}) => {
  const [duration, setDuration] = useState("1 Hour");
  const [numberOfLessons, setNumberOfLessons] = useState("1 Lesson");
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [selectedStudent, setSelectedStudent] = useState("");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [bookingData, setBookingData] = useState<any>(null);
  const [isLessonModalOpen, setIsBookLessonModalOpen] = useState(false);
  const [tutorData, setTutorData] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const { t } = useTranslation();
  const [studentDropdownOpen, setStudentDropdownOpen] = useState(false);
  const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Toggle functions
  const toggleStudentDropdown = () => setStudentDropdownOpen(!studentDropdownOpen);
  const toggleSubjectDropdown = () => setSubjectDropdownOpen(!subjectDropdownOpen);
  const toggleBookLessonModal = () => setIsBookLessonModalOpen(!isLessonModalOpen);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setDate(new Date().toISOString().split("T")[0]);
      setStartTime("");
      setEndTime("");
      setError("");
      setBookingData(null);
    }
  }, [isOpen]);

  // Form validation
  const isFormValid = date && startTime && endTime && selectedStudent && selectedSubject && !error;

  // Fetch data (subjects, students, tutor data)
  useEffect(() => {
    const fetchSubjects = async () => {
      try {
        const response = await axios.get("https://api.nadwa-uae.com/api/v2/tutor/profile/", {
          headers: { Authorization: `Token ${localStorage.getItem("TH_AUTH_TOKEN")}` },
        });
        setSubjects(response.data.subjects);
      } catch (err) {
        console.log(err);
      }
    };
    fetchSubjects();
  }, [isOpen]);

  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await axios.get("https://api.nadwa-uae.com/api/v2/chat/", {
          headers: { Authorization: `Token ${localStorage.getItem("TH_AUTH_TOKEN")}` },
        });
        setStudents(response.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchStudents();
  }, [isOpen]);

  useEffect(() => {
    const fetchTutorData = async () => {
      try {
        const response = await axios.get("https://api.nadwa-uae.com/api/v2/tutor/profile/", {
          headers: { Authorization: `Token ${localStorage.getItem("TH_AUTH_TOKEN")}` },
        });
        setTutorData(response.data);
      } catch (err) {
        console.log(err);
      }
    };
    fetchTutorData();
  }, [isOpen]);

  const calculateEndTime = (start: string, duration: string) => {
    const [hours, minutes] = start.split(":").map(Number);
    let endHours = hours;
    let endMinutes = minutes;

    if (duration === "1 Hour") {
      endHours += 1;
    } else if (duration === "90 Minutes") {
      endMinutes += 90;
      if (endMinutes >= 60) {
        endMinutes -= 60;
        endHours += 1;
      }
    } else if (duration === "2 Hours") {
      endHours += 2;
    }

    if (endMinutes >= 60) {
      endMinutes -= 60;
      endHours += 1;
    }
    if (endHours >= 24) {
      endHours -= 24;
    }

    return `${endHours?.toString().padStart(2, "0")}:${endMinutes?.toString().padStart(2, "0")}`;
  };

  const validateTime = (selectedDate: string, selectedStartTime: string): boolean => {
    const now = DateTime.now().setZone(timezone);
    const selected = DateTime.fromISO(`${selectedDate}T${selectedStartTime}:00`, { zone: timezone });
    return selected > now;
  };

  const handleStartTimeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newStartTime = e.target.value;
    setStartTime(newStartTime);
    setEndTime(calculateEndTime(newStartTime, duration));
    if (!validateTime(date, newStartTime)) {
      setError("Selected time is in the past. Please choose a future time.");
    } else {
      setError("");
    }
  };

  const handleDurationChange = (durationOption: string) => {
    setDuration(durationOption);
    if (startTime) {
      setEndTime(calculateEndTime(startTime, durationOption));
    }
  };

  const handleBookLesson = () => {
    if (!isFormValid) return;

    setIsLoading(true);
    setError("");

    const selectedCustomer = students.find(
      (student) => student.customer?.first_name === selectedStudent
    );
    const selectedSubjectObj = subjects.find(
      (subject) => subject.name === selectedSubject
    );

    const numLessons = parseInt(numberOfLessons.split(" ")[0]);
    const baseDate = DateTime.fromISO(date, { zone: timezone });
    const lessons = [];

    // Generate recurring lessons for display
    for (let i = 0; i < numLessons; i++) {
      const lessonDate = baseDate.plus({ weeks: i });
      const localDateString = lessonDate.toISODate();
      const localStartTime = lessonDate
        .set({ hour: parseInt(startTime.split(":")[0]), minute: parseInt(startTime.split(":")[1]) })
        .toISO({ suppressMilliseconds: true, includeOffset: false });
      const localEndTime = lessonDate
        .set({ hour: parseInt(endTime.split(":")[0]), minute: parseInt(endTime.split(":")[1]) })
        .toISO({ suppressMilliseconds: true, includeOffset: false });

      lessons.push({
        start_time: localStartTime, // e.g., "2025-03-21T19:30:00"
        end_time: localEndTime,     // e.g., "2025-03-21T20:30:00"
        day: lessonDate.toLocaleString(DateTime.DATE_FULL, { locale: "en-US" }).split(",")[0],
        date: localDateString,
      });
    }

    // Format start_time and end_time in the user's timezone with offset
    const startDt = DateTime.fromISO(`${date}T${startTime}:00`, { zone: timezone });
    const endDt = DateTime.fromISO(`${date}T${endTime}:00`, { zone: timezone });
    const start_time = startDt.toISO({ suppressMilliseconds: true }); // e.g., "2025-03-21T19:30:00+04:00"
    const end_time = endDt.toISO({ suppressMilliseconds: true });     // e.g., "2025-03-21T20:30:00+04:00"

    const bookingData = {
      customer: selectedCustomer?.customer.id,
      location: "online",
      subject: selectedSubjectObj?.id,
      tutor: tutorData.user?.id,
      num_weeks: numLessons,
      start_time,
      end_time,
      lessons,
    };

    console.log("Booking Data:", bookingData);
    setBookingData(bookingData);
    toggleBookLessonModal();
    setIsLoading(false);
  };

  if (!isOpen) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const lessonOptions = ["1 Lesson", "5 Lessons", "10 Lessons", "15 Lessons", "20 Lessons"];

  const generateTimeOptions = () => {
    let options = [];
    for (let hour = 6; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedMinute = minute.toString().padStart(2, "0");
        const time = `${hour.toString().padStart(2, "0")}:${formattedMinute}`;
        options.push(
          <option key={time} value={time}>
            {time}
          </option>
        );
      }
    }
    return options;
  };

  return (
    <div
      onClick={handleOverlayClick}
      className="fixed inset-0 bg-gray-950 bg-opacity-50 z-[9999] flex items-center justify-center p-2 sm:p-4"
    >
      <div className="bg-white rounded-lg w-full max-w-xl p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4 sm:mb-6">
          <h2 className="text-xl sm:text-2xl font-semibold">{t("book_a_lesson")}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 cursor-pointer hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 sm:space-y-6">
          {/* Student Selection */}
          <div>
            <label className="block text-base sm:text-lg font-medium mb-2">
              {t("book_lesson_student")}
            </label>
            <div className="relative">
              <select
                className="w-full p-2 border rounded-md appearance-none pr-10 text-sm sm:text-base text-gray-500"
                value={selectedStudent}
                onChange={(e) => setSelectedStudent(e.target.value)}
                onClick={toggleStudentDropdown}
              >
                <option value="">{t("select_students")}</option>
                {students.map((student: any) => (
                  <option key={student.id} value={student.customer.first_name}>
                    {student.customer.first_name}
                  </option>
                ))}
              </select>
              <div className="absolute top-0 right-0 p-2 text-gray-500">
                {studentDropdownOpen ? <ChevronUp /> : <ChevronDown />}
              </div>
            </div>
          </div>

          {/* Subject Selection */}
          <div>
            <label className="block text-base sm:text-lg font-medium mb-2">
              {t("subject")}
            </label>
            <div className="relative">
              <select
                className="w-full p-2 border rounded-md appearance-none pr-10 text-sm sm:text-base text-gray-500"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                onClick={toggleSubjectDropdown}
              >
                <option value="">{t("select_subject")}</option>
                {subjects.map((subject: any) => (
                  <option key={subject.id} value={subject.name}>
                    {subject.name}{" "}
                    {subject.price_in_cents
                      ? `  AED ${(subject.price_in_cents / 100).toFixed(2)} /hour`
                      : null}
                  </option>
                ))}
              </select>
              <div className="absolute top-0 right-0 p-2 text-gray-500">
                {subjectDropdownOpen ? <ChevronUp /> : <ChevronDown />}
              </div>
            </div>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-base sm:text-lg font-medium mb-2">
              {t("select_a_date")}
            </label>
            <div>
              <input
                type="date"
                className="w-full p-2 border rounded-md pr-10 text-sm sm:text-base"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                onFocus={(e) => e.target?.showPicker()}
              />
            </div>
          </div>

          {/* Duration Selection */}
          <div>
            <label className="block text-base sm:text-lg font-medium mb-2">
              {t("select_duration")}
            </label>
            <div className="flex gap-2 flex-wrap justify-start">
              {[t("one_hour"), t("ninety_minutes"), t("two_hours")].map((durationOption) => (
                <button
                  key={durationOption}
                  className={`px-3 sm:px-4 py-1 text-sm sm:text-base rounded ${duration === durationOption
                    ? "bg-[#008847] text-white"
                    : "bg-green-50 border-1 border-[#008847] text-[#008847]"
                    }`}
                  onClick={() => {
                    handleDurationChange(durationOption);
                    setDuration(durationOption);
                    const newEndTime = calculateEndTime(startTime, durationOption);
                    setEndTime(newEndTime);
                  }}
                >
                  {durationOption}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-2 relative">
            <h3 className="text-base sm:text-lg font-medium mb-2">{t("number_weeks")}</h3>
            <div
              className="relative flex items-center"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <TriangleAlert className="w-5 h-5 text-[#008847] mb-2 cursor-pointer" />
              {showTooltip && (
                <div className="absolute right-2 -top-12 bg-[#008847] text-white text-xs p-2 rounded-md shadow-lg w-48">
                  We recommend 10 lessons to get started - why not suggest this to your student...
                  <div className="absolute right-4 -bottom-2 w-3 h-3 bg-[#008847] rotate-45"></div>
                </div>
              )}
            </div>
          </div>

          {/* Lesson Selection */}
          <div>
            <label className="block text-base sm:text-lg font-medium mb-2">
              {t("number_lessons")}
            </label>
            <div className="relative">
              <select
                className="w-full p-2 border rounded-md appearance-none pr-10 text-sm sm:text-base text-gray-500"
                value={numberOfLessons}
                onChange={(e) => setNumberOfLessons(e.target.value)}
              >
                {lessonOptions.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Time Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-base sm:text-lg font-medium mb-2">
                {t("start_time")}
              </label>
              <div className="relative">
                <select
                  className="w-full p-2 border rounded-md pr-10 text-sm sm:text-base"
                  value={startTime}
                  onChange={handleStartTimeChange}
                >
                  <option value="">{t("select_start_time")}</option>
                  {generateTimeOptions()}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-base sm:text-lg font-medium mb-2">
                {t("end_time")}
              </label>
              <div className="relative">
                <select
                  className="w-full p-2 border rounded-md pr-10 text-sm sm:text-base cursor-not-allowed"
                  value={endTime}
                  disabled
                >
                  <option value="">{t("select_end_time")}</option>
                  {generateTimeOptions()}
                </select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-4 sm:mt-6">
            <button
              onClick={onClose}
              className="w-full sm:w-auto py-2 px-4 text-sm sm:text-base font-semibold border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              {t("cancel")}
            </button>
            <button
              onClick={handleBookLesson}
              className={`w-full sm:w-auto py-2 px-4 text-sm sm:text-base font-semibold rounded-md text-white ${isFormValid && !isLoading
                ? "bg-[#008847] hover:bg-[#006c36]"
                : "bg-gray-400 cursor-not-allowed"
                }`}
              disabled={!isFormValid || isLoading}
            >
              {isLoading ? "Booking..." : t("book_lesson")}
            </button>
          </div>
        </div>
      </div>

      {isLessonModalOpen && (
        <BookingPopup
          bookingData={bookingData}
          onClose={toggleBookLessonModal}
          onCloseBack={onClose}
          setUpdateUi={setUpdateUi}
          updateUi={updateUi}
          timeZone={timezone}
        />
      )}
    </div>
  );
};

export default BookLessonModal;